import { Link } from "react-router-dom";
import { ArrowLeft, Building2, User } from "lucide-react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FORM_FILING_TYPE, type FormFilingType } from "../types/wizard.types";
import { useWizard } from "../context/WizardContext";
import { cn } from "@/lib/utils";

const options: { type: FormFilingType; title: string; description: string; icon: React.ReactNode }[] = [
  {
    type: FORM_FILING_TYPE.INDIVIDUAL,
    title: "Individual Filing",
    description: "Personal income, W-2s, dependents, and health coverage.",
    icon: <User className="h-10 w-10 text-primary" />,
  },
  {
    type: FORM_FILING_TYPE.BUSINESS,
    title: "Business Filing",
    description: "Schedule C, income, expenses, and business details.",
    icon: <Building2 className="h-10 w-10 text-primary" />,
  },
];

export function FormTypeSelection() {
  const { setFormType } = useWizard();

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-8">
      <Button variant="ghost" size="sm" className="-ml-2 mb-2" asChild>
        <Link to="/dashboard">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to dashboard
        </Link>
      </Button>
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Choose your filing type
        </h1>
        <p className="text-muted-foreground">
          Select the type of tax form you want to complete. You can start a new filing anytime.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {options.map((opt) => (
          <Card
            key={opt.type}
            role="button"
            tabIndex={0}
            onClick={() => setFormType(opt.type)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setFormType(opt.type);
              }
            }}
            className={cn(
              "cursor-pointer transition-all hover:border-primary hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            )}
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted">
                  {opt.icon}
                </div>
                <div className="space-y-1.5">
                  <CardTitle className="text-lg">{opt.title}</CardTitle>
                  <CardDescription className="text-sm">{opt.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>
    </div>
  );
}
